import Iconify from "@/components/ui/Iconify";
import StaticVideoPreview from "@/components/ui/StaticVideoPreview";
import { useTranslations } from "@/lib/static-intl";
import Image from "next/image";
import { useEffect, useState } from "react";
import PlaceImage from "@/components/place-image";
import {
  dedupeMediaRecords,
  getMediaRecordUrl,
  getPlaceImageUrl,
  hasUsableMediaRecordPath,
  normalizeMediaRecordForGrid,
} from "@/utils/media";
import { getAllPlaceMediaForWindowByPlaceId } from "@/api/places";
import { enqueueSnackbar } from "notistack";

const EMPTY_INITIAL_MEDIA = [];

const PDW_PlaceMediaPhotoGrid = ({
  placeId,
  initialMedia = EMPTY_INITIAL_MEDIA,
  onClick,
  className,
  setPlaceMedia,
}) => {
  const t = useTranslations("places.place_details_window");
  const apiT = useTranslations("api");
  const [media, setMedia] = useState([]);
  const [loader, setLoader] = useState(true);

  const getImageSizes = (index) =>
    index === 0
      ? "(max-width: 640px) 50vw, (max-width: 1024px) 380px, 520px"
      : "(max-width: 640px) 25vw, (max-width: 1024px) 190px, 260px";

  const renderMedia = (mediaItem, index) => {
    const mediaUrl = getMediaRecordUrl(mediaItem);
    const imageUrl = getPlaceImageUrl(mediaItem);
    const posterUrl =
      mediaItem?.thumbnailPath ||
      mediaItem?.placeholderPath ||
      mediaItem?.placeholder
        ? getMediaRecordUrl(mediaItem, { variant: "thumbnail" })
        : undefined;

    if (mediaItem?.mediaType?.includes("video")) {
      return (
        <StaticVideoPreview
          key={index}
          src={mediaUrl}
          poster={posterUrl}
          className="object-cover w-full h-full"
          onClick={() => {
            if (mediaItem?.type === "menu") onClick("Menu");
            else onClick("Photos");
          }}
        />
      );
    } else {
      return (
        <PlaceImage
          key={index}
          src={imageUrl}
          width={index === 0 ? 720 : 420}
          height={index === 0 ? 720 : 420}
          sizes={getImageSizes(index)}
          quality={90}
          alt="media"
          className="w-full h-full object-cover cursor-pointer"
          priority={index === 0}
          onClick={() => {
            if (mediaItem?.type === "menu") onClick("Menu");
            else onClick("Photos");
          }}
        />
      );
    }
  };

  const fetchAllPlaceMedia = async (placeId) => {
    try {
      const response = await getAllPlaceMediaForWindowByPlaceId(placeId);
      if (Array.isArray(response)) {
        const filteredMedia = dedupeMediaRecords(
          response.filter(hasUsableMediaRecordPath).map(normalizeMediaRecordForGrid)
        );
        if (filteredMedia.length > 0) {
          setPlaceMedia?.(filteredMedia);
          setMedia(filteredMedia);
        }
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        {
          variant: "error",
        }
      );
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    const usableInitialMedia = Array.isArray(initialMedia)
      ? dedupeMediaRecords(
          initialMedia.filter(hasUsableMediaRecordPath).map(normalizeMediaRecordForGrid)
        )
      : [];

    if (usableInitialMedia.length > 0) {
      setPlaceMedia?.(usableInitialMedia);
      setMedia(usableInitialMedia);
      setLoader(false);
    } else {
      setMedia([]);
      setPlaceMedia?.([]);
      setLoader(true);
      if (placeId) fetchAllPlaceMedia(placeId);
    }
  }, [placeId, initialMedia]);

  if (loader) {
    return (
      <div
        className={`-mt-16 w-full h-[300px] flex flex-col items-center justify-center gap-1 dark:bg-white/[0.06] text-sm text-black/40 dark:text-white/50 animate-pulse bg-black/5`}
      />
    );
  }

  if (media?.length === 0) return null;

  return (
    <>
      {media?.length >= 5 ? (
        <div
          className={`w-full grid grid-cols-4 grid-rows-2 border-b dark:border-brown ${className}`}
        >
          <div className="col-span-2 row-span-2 relative">
            {media[0]
              ? renderMedia(media[0], 0)
              : renderMedia(media[media.length - 1], media.length - 1)}
          </div>
          <div className="col-start-3 relative">
            {media[1] ? (
              renderMedia(media[1], 1)
            ) : (
              <div className="w-full h-full flex flex-col gap-1 items-center justify-center group cursor-pointer bg-black/5 dark:bg-white/[0.06] relative">
                <Image
                  src={"/no-data.png"}
                  alt="no-data"
                  width={30}
                  height={30}
                />
                <span className="text-[11px] text-center opacity-60">
                  {t("no location image")}
                </span>
              </div>
            )}
          </div>
          <div className="col-start-4 relative">
            {media[2] ? (
              renderMedia(media[2], 2)
            ) : (
              <div className="w-full h-full flex flex-col gap-1 items-center justify-center group cursor-pointer bg-black/5 dark:bg-white/[0.06] relative">
                <Image
                  src={"/no-data.png"}
                  alt="no-data"
                  width={30}
                  height={30}
                />
                <span className="text-[11px] text-center opacity-60">
                  {t("no location image")}
                </span>
              </div>
            )}
          </div>
          <div className="col-start-3 row-start-2 relative">
            {media[3] ? (
              renderMedia(media[3], 3)
            ) : (
              <div className="w-full h-full flex flex-col gap-1 items-center justify-center group cursor-pointer bg-black/5 dark:bg-white/[0.06] relative">
                <Image
                  src={"/no-data.png"}
                  alt="no-data"
                  width={30}
                  height={30}
                />
                <span className="text-[11px] text-center opacity-60">
                  {t("no location image")}
                </span>
              </div>
            )}
          </div>
          <div className="col-start-4 row-start-2 relative">
            {media[4] ? (
              renderMedia(media[4], 4)
            ) : (
              <div className="w-full h-full flex flex-col gap-1 items-center justify-center group cursor-pointer bg-black/5 dark:bg-white/[0.06] relative">
                <Image
                  src={"/no-data.png"}
                  alt="no-data"
                  width={30}
                  height={30}
                />
                <span className="text-[11px] text-center opacity-60">
                  {t("no location image")}
                </span>
              </div>
            )}

            {media?.length > 5 && (
              <div
                className="absolute top-0 left-0 right-0 bottom-0 bg-black flex flex-col items-center justify-center text-white opacity-60"
                onClick={() => {
                  if (media[4]?.type === "menu") onClick("Menu");
                  else onClick("Photos");
                }}
              >
                <Iconify
                  icon={"material-symbols-light:image-outline"}
                  className={"!h-4 !min-w-4 !text-white text-shadow"}
                />
                <span className="text-xs text-shadow">
                  +{media?.length - 5}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {media?.length === 1 ? (
            <div
              className={`w-full grid grid-cols-1 grid-rows-1 border-b dark:border-brown ${className}`}
            >
              <div className="relative">{renderMedia(media[0], 0)}</div>
            </div>
          ) : (
            <div
              className={`w-full grid grid-cols-2 grid-rows-1 border-b dark:border-brown ${className}`}
            >
              <div className="relative">{renderMedia(media[0], 0)}</div>
              <div className="relative">{renderMedia(media[1], 1)}</div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default PDW_PlaceMediaPhotoGrid;
